import { CreditCard, Users, FileCheck } from "lucide-react";
import { Link } from "react-router-dom";
import ServicePageLayout from "../components/ServicePageLayout";

const prestations = [
    {
        icon: CreditCard,
        title: "Gestion de paie",
        desc: "Établissement des fiches de salaire, décomptes mensuels, traitement des heures supplémentaires et des indemnités.",
    },
    {
        icon: Users,
        title: "Charges sociales",
        desc: "Décomptes AVS, LPP, AC, LAA et APG. Relations avec les caisses sociales et transmission des rapports annuels.",
    },
    {
        icon: FileCheck,
        title: "Permis de travail et conformité",
        desc: "Accompagnement pour les permis de travail, annonces de personnel étranger et conformité avec le droit du travail genevois.",
    },
];

const etapes = [
    {
        num: "01",
        title: "Premier entretien gratuit",
        desc: "Pour comprendre votre structure et vos effectifs.",
    },
    {
        num: "02",
        title: "Mise en place de la paie",
        desc: "Affiliation aux caisses sociales et organisation des flux.",
    },
    {
        num: "03",
        title: "Traitement mensuel",
        desc: "Traitement mensuel des salaires et gestion des rapports annuels.",
    },
];

const pourquoiCols = [
    {
        title: "Fiabilité absolue",
        desc: "Les salaires sont traités avec précision et dans les délais. Vos employés sont payés correctement, chaque mois.",
    },
    {
        title: "Conformité sociale",
        desc: "Fiduciaire Bleich assure la conformité avec l'ensemble des obligations sociales suisses et cantonales genevoises.",
    },
    {
        title: "Un seul interlocuteur",
        desc: (
            <>
                Salaires,{" "}
                <Link
                    to="/comptabilite"
                    data-testid="link-comptabilite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    comptabilité
                </Link>{" "}
                et{" "}
                <Link
                    to="/fiscalite"
                    data-testid="link-fiscalite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    fiscalité
                </Link>{" "}
                sont gérés conjointement. Pas de communication à coordonner entre plusieurs prestataires.
            </>
        ),
    },
];

const faqs = [
    {
        q: "Quelles charges sociales sont obligatoires pour un employeur en Suisse ?",
        a: "Un employeur cotise notamment à l'AVS, à l'AI, aux APG, à l'assurance chômage, à la prévoyance professionnelle et à l'assurance accidents, auxquelles s'ajoutent les allocations familiales. Fiduciaire Bleich établit l'ensemble de ces décomptes.",
    },
    {
        q: "Qui paie les cotisations sociales, l'employeur ou l'employé ?",
        a: "Plusieurs cotisations sont partagées entre l'employeur et l'employé, l'employeur retenant la part de l'employé sur le salaire et reversant le tout aux caisses. Fiduciaire Bleich calcule les parts et gère les versements.",
    },
    {
        q: "Faut-il un permis pour engager un collaborateur étranger à Genève ?",
        a: "Cela dépend de la nationalité et du statut de la personne. Les démarches diffèrent selon les cas. Fiduciaire Bleich vous accompagne dans les annonces et les demandes de permis.",
    },
    {
        q: "Fiduciaire Bleich s'occupe-t-elle des rapports avec les caisses sociales ?",
        a: "Oui. Fiduciaire Bleich gère les relations avec les caisses et transmet les décomptes et rapports annuels requis.",
    },
];

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Salaires et RH à Genève",
    serviceType: "Gestion de paie, charges sociales et administration du personnel",
    provider: {
        "@type": "AccountingService",
        name: "Fiduciaire Bleich Sàrl",
        url: "https://fiduciaire-bleich.ch",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Canton de Genève" },
    url: "https://fiduciaire-bleich.ch/salaires-rh",
};

const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 2, name: "Nos services", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 3, name: "Salaires et RH", item: "https://fiduciaire-bleich.ch/salaires-rh" },
    ],
};

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const SalairesRh = () => {
    return (
        <ServicePageLayout
            testIdPrefix="salaires-rh"
            seo={{
                title: "Salaires et RH à Genève | Fiduciaire Bleich",
                description:
                    "Gestion de paie, charges sociales, permis de travail et conformité RH à Genève. Fiduciaire Bleich s'occupe de tout depuis 2004. Premier entretien gratuit.",
                canonical: "https://fiduciaire-bleich.ch/salaires-rh",
            }}
            jsonLd={{ service: serviceJsonLd, breadcrumb: breadcrumbJsonLd, faq: faqJsonLd }}
            breadcrumb="Salaires et RH"
            hero={{
                eyebrow: "Nos services",
                h1Prefix: "Salaires et RH à",
                h1Italic: "Genève.",
                subtitle: "Une administration du personnel rigoureuse et sans faille.",
                intro:
                    "La gestion des salaires et des ressources humaines exige rigueur et connaissance des obligations sociales suisses. Fiduciaire Bleich prend en charge l'ensemble de l'administration du personnel, des décomptes de salaire aux rapports avec les caisses sociales.",
            }}
            prestations={prestations}
            prestationsHeading={
                <>
                    Ce que <span className="italic">nous faisons.</span>
                </>
            }
            etapes={etapes}
            processHeading={
                <>
                    Une administration du personnel <span className="italic">sereine.</span>
                </>
            }
            pourquoiHeading={
                <>
                    La sérénité pour vous,
                    <br />
                    la <span className="italic">rigueur pour vos employés.</span>
                </>
            }
            pourquoiCols={pourquoiCols}
            faqHeading={
                <>
                    Questions fréquentes
                    <br />
                    sur les <span className="italic">salaires et les RH.</span>
                </>
            }
            faqs={faqs}
            cta={{
                heading: (
                    <>
                        Parlons de vos <span className="italic">salaires.</span>
                    </>
                ),
                text: "Un premier entretien gratuit et confidentiel permet d'évaluer vos besoins en gestion de paie et la manière dont Fiduciaire Bleich peut vous décharger. Sans engagement.",
            }}
        />
    );
};

export default SalairesRh;
